/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Action } from '@reduxjs/toolkit';
import { useCallback } from 'react';
import type {
  NotificationScopeEvent,
  PluginManager,
  ScopeEventMap,
  ScopeName,
} from '@opengeoweb/plugin-interface';
import { snackbarActions, snackbarTypes } from '@opengeoweb/snackbar';
import { genericActions, mapActions } from '@opengeoweb/store';
import { useDispatchOnScopeEvent } from '../../hooks';

interface UsePluginToReduxOptions {
  pluginBridge: PluginManager;
  sourceId: string;
  hasScope: (scope: ScopeName) => boolean;
}

export const usePluginToRedux = ({
  pluginBridge,
  sourceId,
  hasScope,
}: UsePluginToReduxOptions): void => {
  const toSnackbarMessage = (
    event: NotificationScopeEvent,
  ): snackbarTypes.SnackbarMessage => {
    let prefix = '';
    if (event.severity === 'ERROR') {
      prefix = 'Error: ';
    } else if (event.severity === 'WARNING') {
      prefix = 'Warning: ';
    }

    return {
      type: snackbarTypes.SnackbarMessageType.VERBATIM_MESSAGE,
      message: `${prefix}${event.content}`,
    };
  };

  const timeEventHandler = useCallback(
    (event: ScopeEventMap['time']): Action =>
      genericActions.setTime({
        sourceId,
        origin: 'PluginBridge',
        value: { currentTime: event.time },
      }),
    [sourceId],
  );

  const animationEventHandler = useCallback(
    (event: ScopeEventMap['animation']): Action | Action[] | null => {
      switch (event.type) {
        case 'setAnimationStartTime':
          return mapActions.setAnimationStartTime({
            mapId: sourceId,
            animationStartTime: event.time,
          });
        case 'setAnimationEndTime':
          return mapActions.setAnimationEndTime({
            mapId: sourceId,
            animationEndTime: event.time,
          });
        case 'setAnimationTimeSpan':
          return [
            mapActions.setAnimationStartTime({
              mapId: sourceId,
              animationStartTime: event.startTime,
            }),
            mapActions.setAnimationEndTime({
              mapId: sourceId,
              animationEndTime: event.endTime,
            }),
          ];
        default:
          return null;
      }
    },
    [sourceId],
  );

  const featureEventHandler = useCallback(
    (event: ScopeEventMap['features']): Action | null => {
      switch (event.type) {
        case 'addLayer': {
          // Anonymous layers get a generated id, meaning the plugin wont get an update back.
          const layerId =
            event.layer.id ?? `pluginLayerId-${crypto.randomUUID()}`;
          return genericActions.addFeatureSyncState({
            panelId: sourceId,
            data: {
              featureLayersById: {
                [layerId]: {
                  layerId,
                  geoJSON: event.layer.featureCollection,
                },
              },
            },
          });
        }
        case 'removeLayer':
          return genericActions.deleteFeatureLayer({
            panelId: sourceId,
            layerId: event.layerId,
          });
        case 'setLayerFeatures':
          return genericActions.addFeatureSyncState({
            panelId: sourceId,
            data: {
              featureLayersById: {
                [event.layerId]: {
                  layerId: event.layerId,
                  geoJSON: event.featureCollection,
                },
              },
            },
          });
        default:
          return null;
      }
    },
    [sourceId],
  );

  const notificationEventHandler = useCallback(
    (event: NotificationScopeEvent): Action | null => {
      if (event.type !== 'showNotification') {
        return null;
      }
      return snackbarActions.openSnackbar(toSnackbarMessage(event));
    },
    [],
  );

  useDispatchOnScopeEvent(
    pluginBridge,
    'time',
    timeEventHandler,
    hasScope('time'),
  );
  useDispatchOnScopeEvent(
    pluginBridge,
    'animation',
    animationEventHandler,
    hasScope('animation'),
  );
  useDispatchOnScopeEvent(
    pluginBridge,
    'features',
    featureEventHandler,
    hasScope('features'),
  );
  useDispatchOnScopeEvent(
    pluginBridge,
    'notification',
    notificationEventHandler,
    hasScope('notification'),
  );
};
